import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { ColorModeContent } from "components/core/colorModeContent"
import { Link } from "components/core/link"
import React, { FC } from "react"
import Image from "next/image"
import dashboardPic from "../public/assets/homepage/dashboard.png"
import dashboardDarkPic from "../public/assets/homepage/dashboardDark.png"
import multipleDashboards from "../public/assets/homepage/multipleDashboards.png"
import multipleDashboardsDark from "../public/assets/homepage/multipleDashboardsDark.png"
import publicDashboardDark from "../public/assets/homepage/publicDashboardDark.png"
import publicDashboard from "../public/assets/homepage/publicDashboard.png"
import privateDashboardDark from "../public/assets/homepage/privateDashboardDark.png"
import privateDashboard from "../public/assets/homepage/privateDashboard.png"
import { useSelectPublicWidgets } from "reactQuery/widget"
import { getBuiltInWidgetConfigByWidget } from "components/dashboard/grid/helpers"
import { RoundedDivider } from "components/core/divider"
import { MarkdownWidget } from "./dashboard/widgets/markdownWidget"
import { Widget } from "domainModels/widget"
import { Card } from "./core/card"
import { Badge } from "components/core/badge"

export const HomePage: FC = () => (
  <>
    <Intro />
    <BuiltInWidgets />
    <CreateOwnWidget />
    <Dashboards />
  </>
)

const Dashboards: FC = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      <RoundedDivider className="text-gray-100 dark:text-[#151d2c]" />

      <div className="hidden dark:block absolute w-3/4 h-1/2 -top-28 left-1/2 -translate-x-1/2 bg-gradient-radial from-indigo-700/10 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto px-4 max-w-3xl sm:px-6 lg:px-12 lg:max-w-[1600px] py-12 lg:py-16 2xl:py-30">
        <div className="text-center">
          <p className="mt-2 text-3xl text-gray-900 dark:text-gray-100 font-extrabold tracking-tight sm:text-4xl">
            Customizable dashboards
          </p>
          <p className="mt-5 max-w-prose mx-auto text-lg lg:text-xl text-gray-500 dark:text-gray-300">
            Unlock the power of organization and collaboration with our dashboard features.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 xl:grid-cols-3 mt-12">
          <div className="overflow-hidden p-px">
            <Card className="rounded-2xl p-8 gap-6 lg:mx-auto lg:max-w-xl group h-full flex flex-col">
              <div>
                <div className="hidden dark:block pointer absolute w-full h-1/2 -top-16 -left-32  bg-gradient-radial from-indigo-600/10 via-transparent to-transparent" />

                <h3 className="relative text-2xl font-medium mb-4 tracking-tight">Multiple dashboards</h3>
                <p className="relative text-gray-500 dark:text-gray-400 mb-12">
                  Create multiple dashboards to track and manage different projects and teams, all in one place.
                </p>
              </div>

              <div className="relative">
                <div className="hidden dark:block pointer-events-none transition-opacity duration-700 opacity-0 group-hover:opacity-100 absolute w-full h-full scale-x-125 scale-y-[2]  bg-gradient-radial from-indigo-600/30 via-transparent to-transparent" />
                <ColorModeContent
                  dark={
                    <Image
                      priority
                      src={multipleDashboardsDark}
                      alt="Multiple Dashboards"
                      width={234}
                      height={261}
                      quality={100}
                      className="relative transform-gpu mx-auto shadow-2xl rounded-2xl transition-opacity duration-500 opacity-60 group-hover:opacity-100"
                      placeholder="blur"
                    />
                  }
                  light={
                    <Image
                      priority
                      src={multipleDashboards}
                      alt="Multiple Dashboards"
                      width={240}
                      height={256}
                      quality={100}
                      className="relative transform-gpu mx-auto shadow-2xl rounded-2xl transition-opacity duration-500 opacity-60 group-hover:opacity-100"
                      placeholder="blur"
                    />
                  }
                />
              </div>
            </Card>
          </div>
          <div className="overflow-hidden p-px">
            <Card className="rounded-2xl p-8 gap-6 group h-full lg:mx-auto lg:max-w-xl">
              <div className="hidden dark:block absolute w-full h-1/2 -top-16 -left-32  bg-gradient-radial from-violet-600/10 via-transparent to-transparent" />
              <h3 className="relative text-2xl font-medium mb-4 tracking-tight">Private dashboards</h3>
              <p className="relative text-gray-500 dark:text-gray-400 mb-12">
                Keep your data secure by making your dashboards private or password protected.
              </p>
              <div className="relative">
                <div className="hidden dark:block pointer-events-none transition-opacity duration-700 opacity-0 group-hover:opacity-100 absolute w-full h-full scale-[2]  bg-gradient-radial from-violet-600/30 via-transparent to-transparent" />

                <ColorModeContent
                  dark={
                    <Image
                      priority
                      src={privateDashboardDark}
                      alt="Private Dashboards"
                      width={528}
                      height={455}
                      quality={100}
                      className="relative transform-gpu mx-auto shadow-2xl rounded-2xl transition-opacity duration-500 opacity-60 group-hover:opacity-100 border-[12px] border-gray-900/50"
                      placeholder="blur"
                    />
                  }
                  light={
                    <Image
                      priority
                      src={privateDashboard}
                      alt="Private Dashboards"
                      width={603}
                      height={465}
                      quality={100}
                      className="relative transform-gpu mx-auto shadow-2xl rounded-2xl transition-opacity duration-500 opacity-60 group-hover:opacity-100"
                      placeholder="blur"
                    />
                  }
                />
              </div>
            </Card>
          </div>
          <div className="overflow-hidden p-px">
            <Card className="rounded-2xl p-8 gap-6 group h-full flex flex-col lg:mx-auto lg:max-w-xl">
              <div>
                <div className="hidden dark:block absolute w-full h-1/2 -top-16 -left-32  bg-gradient-radial from-indigo-600/10 via-transparent to-transparent" />
                <h3 className="relative text-2xl font-medium mb-4 tracking-tight">Share dashboard</h3>
                <p className="relative text-gray-500 dark:text-gray-400 mb-12">
                  Easily share your public dashboards with anyone, simply by sending a link.
                </p>
              </div>

              <div className="relative">
                <div className="hidden dark:block pointer-events-none transition-opacity duration-700 opacity-0 group-hover:opacity-100 absolute w-full h-full scale-[2]  bg-gradient-radial from-indigo-600/30 via-transparent to-transparent" />

                <ColorModeContent
                  dark={
                    <Image
                      priority
                      src={publicDashboardDark}
                      alt="Public Dashboards"
                      width={593}
                      height={416}
                      quality={100}
                      className="relative transform-gpu mx-auto shadow-2xl rounded-2xl transition-opacity duration-500 opacity-60 group-hover:opacity-100 border-[12px] border-gray-900/50"
                      placeholder="blur"
                    />
                  }
                  light={
                    <Image
                      priority
                      src={publicDashboard}
                      alt="Public Dashboards"
                      width={596}
                      height={413}
                      quality={100}
                      className="relative transform-gpu mx-auto shadow-2xl rounded-2xl transition-opacity duration-500 opacity-60 group-hover:opacity-100"
                      placeholder="blur"
                    />
                  }
                />
              </div>
            </Card>
          </div>
        </div>
      </div>

      <div className="relative text-center z-20 mx-auto px-4 max-w-3xl sm:px-6 lg:px-12 lg:max-w-7xl pt-4 pb-16 2xl:py-30">
        <p className="text-3xl text-gray-900 dark:text-gray-100 font-extrabold tracking-tight sm:text-4xl">Ready?</p>
        <p className="my-6 text-lg lg:text-xl text-gray-500 dark:text-gray-300">
          Create now your free account and use the full potential of Frontend Toolkit.
        </p>
        <Link to="/auth/registration" variant="primary" size="big">
          Get started
        </Link>
      </div>
    </div>
  )
}

const Intro: FC = () => {
  return (
    <div className="relative -mt-px bg-gradient-to-b from-indigo-800 to-indigo-900 pt-6 pb-12 lg:py-16 2xl:py-30 overflow-hidden">
      <div className="relative z-10 lg:grid lg:grid-cols-2 lg:gap-24 max-w-3xl lg:max-w-7xl xl:max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div>
          <h1 className="font font-extrabold tracking-tight text-gray-100 text-4xl lg:text-5xl xl:text-6xl">
            Create dashboards for your development needs
          </h1>
          <p className="mt-6 mb-8 text-lg lg:text-xl text-gray-300">
            Frontend Toolkit helps you organize everything you need in your daily work in one place. Without annoying
            ads and fully customizable. Say goodbye to cluttered websites and hello to a streamlined experience.
          </p>

          <Link to="/dashboard" variant="primary" size="big">
            <span className="font-medium text-base">Go to your dashboard</span>
            <ChevronRightIcon className="ml-3 h-5 w-5 mt-0.5 -mr-1" />
          </Link>
        </div>
        <div className="mt-12 lg:mt-0 relative w-[560px] h-[500px] mx-auto">
          <div className="absolute -inset-x-16 top-1/2 h-1/2 bg-gradient-to-b from-violet-600 to-gray-[#151d2c] opacity-80 blur-3xl"></div>

          <div className="relative shadow-2xl rounded-2xl w-full h-full overflow-hidden">
            <ColorModeContent
              dark={<Image priority src={dashboardDarkPic} alt="Dashboard" fill quality={100} placeholder="blur" />}
              light={<Image priority src={dashboardPic} alt="Dashboard" fill quality={100} placeholder="blur" />}
            />
            <div className="absolute bottom-4 left-4 xl:left-auto xl:right-4">
              <Link
                to="https://www.producthunt.com/posts/frontend-toolkit?utm_source=badge-top-post-badge&utm_medium=badge&utm_souce=badge-frontend-toolkit"
                target="_blank"
                rel="noreferrer noopener"
                className="!block p-1 !rounded-xl transition-all duration-300 bg-gray-400/20 hover:bg-gray-400/40 dark:bg-violet-500/30 hover:dark:bg-violet-500/50 hover:-translate-y-0.5"
              >
                <ColorModeContent
                  dark={
                    <svg xmlns="http://www.w3.org/2000/svg" width={250} height={54}>
                      <g fill="none" fillRule="evenodd">
                        <rect fill="#24273f" x={0.5} y={0.5} width={249} height={53} rx={10} />
                        <text fontFamily="Helvetica-Bold, Helvetica" fontSize={9} fontWeight="bold" fill="#FFF">
                          <tspan x={53} y={20}>
                            {"Product Hunt"}
                          </tspan>
                        </text>
                        <text fontFamily="Helvetica-Bold, Helvetica" fontSize={16} fontWeight="bold" fill="#FFF">
                          <tspan x={52} y={40}>
                            {"#1 Product of the Day"}
                          </tspan>
                        </text>
                        <g transform="translate(17 13)">
                          <path
                            d="M4.33 16.364L.328 24.9c-.126.258.007.42.427.34l3.258-.708a.244.244 0 01.317.17l1.467 3.007c.14.324.302.39.428.133l4.26-8.934-6.155-2.544zM16.67 16.364l4.002 8.536c.133.258-.007.42-.427.34l-3.258-.708a.246.246 0 00-.317.17l-1.467 3.007c-.14.324-.295.39-.428.133l-4.26-8.934 6.155-2.544z"
                            fill="#DE7818"
                            fillRule="nonzero"
                          />
                          <path
                            d="M9.298 21.392c0 .007-.014 0-.029 0a10.44 10.44 0 01-5.97-2.742c-.008-.008-.03-.015-.023-.023l.184-.39c.008-.015.022.022.03.03 1.577 1.466 3.767 2.388 6.007 2.66.008 0 .023 0 .023.008l-.222.457z"
                            fill="#B35454"
                            fillRule="nonzero"
                          />
                          <circle fill="#F0CD0A" fillRule="nonzero" cx={10.5} cy={10.489} r={10.489} />
                          <circle fill="#D2B309" fillRule="nonzero" cx={10.5} cy={10.489} r={9.045} />
                          <circle fill="#E3C101" fillRule="nonzero" cx={10.75} cy={10.75} r={8.75} />
                          <path d="M10.45 14.755h2.15V6.3h-2.15L8.3 7.765v1.851l2.04-1.377h.11z" fill="#FFF2B5" />
                          <path
                            d="M12.97 3.089a7.604 7.604 0 01.353 14.698c2.035-1.555 3.384-4.209 3.384-7.224 0-3.184-1.504-5.963-3.738-7.474z"
                            fillOpacity={0.2}
                            fill="#FFF"
                            fillRule="nonzero"
                          />
                          <path
                            d="M11.702 21.392c.007.007.022 0 .029 0 2.293-.288 4.4-1.21 5.986-2.728.007-.007.029-.014.022-.022l-.185-.39c-.007-.015-.022.022-.03.029-1.577 1.467-3.773 2.374-6.021 2.646-.008 0-.023 0-.023.008l.222.457z"
                            fill="#B35454"
                            fillRule="nonzero"
                          />
                        </g>
                      </g>
                    </svg>
                  }
                  light={
                    <svg xmlns="http://www.w3.org/2000/svg" width={250} height={54}>
                      <g fill="none" fillRule="evenodd">
                        <rect stroke="#EA532A" fill="#FFF" x={0.5} y={0.5} width={249} height={53} rx={10} />
                        <text fontFamily="Helvetica-Bold, Helvetica" fontSize={9} fontWeight="bold" fill="#EA532A">
                          <tspan x={53} y={20}>
                            {"Product Hunt"}
                          </tspan>
                        </text>
                        <text fontFamily="Helvetica-Bold, Helvetica" fontSize={16} fontWeight="bold" fill="#EA532A">
                          <tspan x={52} y={40}>
                            {"#1 Product of the Day"}
                          </tspan>
                        </text>
                        <g transform="translate(17 13)">
                          <path
                            d="M4.33 16.364L.328 24.9c-.126.258.007.42.427.34l3.258-.708a.244.244 0 01.317.17l1.467 3.007c.14.324.302.39.428.133l4.26-8.934-6.155-2.544zM16.67 16.364l4.002 8.536c.133.258-.007.42-.427.34l-3.258-.708a.246.246 0 00-.317.17l-1.467 3.007c-.14.324-.295.39-.428.133l-4.26-8.934 6.155-2.544z"
                            fill="#DE7818"
                            fillRule="nonzero"
                          />
                          <path
                            d="M9.298 21.392c0 .007-.014 0-.029 0a10.44 10.44 0 01-5.97-2.742c-.008-.008-.03-.015-.023-.023l.184-.39c.008-.015.022.022.03.03 1.577 1.466 3.767 2.388 6.007 2.66.008 0 .023 0 .023.008l-.222.457z"
                            fill="#B35454"
                            fillRule="nonzero"
                          />
                          <circle fill="#F0CD0A" fillRule="nonzero" cx={10.5} cy={10.489} r={10.489} />
                          <circle fill="#D2B309" fillRule="nonzero" cx={10.5} cy={10.489} r={9.045} />
                          <circle fill="#E3C101" fillRule="nonzero" cx={10.75} cy={10.75} r={8.75} />
                          <path d="M10.45 14.755h2.15V6.3h-2.15L8.3 7.765v1.851l2.04-1.377h.11z" fill="#FFF2B5" />
                          <path
                            d="M12.97 3.089a7.604 7.604 0 01.353 14.698c2.035-1.555 3.384-4.209 3.384-7.224 0-3.184-1.504-5.963-3.738-7.474z"
                            fillOpacity={0.2}
                            fill="#FFF"
                            fillRule="nonzero"
                          />
                          <path
                            d="M11.702 21.392c.007.007.022 0 .029 0 2.293-.288 4.4-1.21 5.986-2.728.007-.007.029-.014.022-.022l-.185-.39c-.007-.015-.022.022-.03.029-1.577 1.467-3.773 2.374-6.021 2.646-.008 0-.023 0-.023.008l.222.457z"
                            fill="#B35454"
                            fillRule="nonzero"
                          />
                        </g>
                      </g>
                    </svg>
                  }
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const BuiltInWidgets: FC = () => {
  const { data: publicWidgets } = useSelectPublicWidgets()

  return (
    <div className="bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      <RoundedDivider className="text-indigo-900" />

      <div className="hidden dark:block absolute w-3/4 h-1/2 -top-28 left-1/2 -translate-x-1/2 bg-gradient-radial from-indigo-700/10 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto px-4 text-center max-w-3xl sm:px-6 lg:px-12 lg:max-w-7xl py-12 lg:py-16 2xl:py-30">
        <p className="mt-2 text-3xl text-gray-900 dark:text-gray-100 font-extrabold tracking-tight sm:text-4xl">
          Prebuilt widgets
        </p>
        <p className="mt-5 max-w-prose mx-auto text-lg lg:text-xl text-gray-500 dark:text-gray-300">
          A set of built-in widgets allows you to do the tedious parts of your work quickly.
        </p>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 mt-12">
          {publicWidgets
            ?.filter((w) => !w.information.external_url)
            .map((widget) => {
              const builtInWidget = getBuiltInWidgetConfigByWidget(widget)

              return (
                <Link
                  key={widget.information.slug}
                  aria-label={widget.information.name}
                  title={widget.information.name}
                  to={`/widget/${widget.information.slug}`}
                  scroll={false}
                  className="transition-transform hover:-translate-y-0.5"
                >
                  <Card className="relative h-full p-8 rounded-lg" showHoverEffect>
                    <div className="flex items-center">
                      <div className="w-8 h-8">{builtInWidget?.icon}</div>
                      <h2 className="text-lg leading-none text-gray-900 dark:text-gray-200 ml-3 flex flex-wrap items-center gap-3">
                        {widget.information.name}
                        {widget.information.slug === "js-converter" && <Badge className="uppercase">AI-enhanced</Badge>}
                      </h2>
                    </div>
                    <p className="mt-5 font-normal text-gray-500 dark:text-gray-400 text-left">
                      {widget.information.description}
                    </p>
                  </Card>
                </Link>
              )
            })}
        </div>
      </div>
    </div>
  )
}

const CreateOwnWidget: FC = () => {
  return (
    <div className="bg-gray-100 dark:bg-[#151d2c] relative overflow-hidden">
      <RoundedDivider className="text-gray-50 dark:text-gray-900" />
      <div className="hidden dark:block absolute w-3/4 h-1/2 -top-28 left-1/2 -translate-x-1/2 bg-gradient-radial from-indigo-600/10 via-transparent to-transparent" />

      <div className="relative z-20 my-16 px-4 sm:px-6 lg:px-12">
        <h2 className="mt-2 text-center text-3xl text-gray-900 dark:text-gray-100 font-extrabold tracking-tight sm:text-4xl">
          Create your own widgets
        </h2>
        <p className="mt-5 text-center max-w-prose mx-auto text-lg lg:text-xl text-gray-500 dark:text-gray-300">
          Missing something that would be useful to have on your dashboard?
        </p>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl -mx-4 sm:mx-auto my-16 px-4 sm:px-6 lg:px-12">
          <Card className="p-6 sm:px-12 sm:py-10 rounded-2xl">
            <div className="hidden dark:block absolute w-full h-1/2 -top-16 -left-32  bg-gradient-radial from-violet-600/10 via-transparent to-transparent" />
            <h3 className="relative text-2xl font-medium mb-4 tracking-tight">Embed websites</h3>
            <p className="relative text-gray-500 dark:text-gray-400 mb-12">
              Easily access your favorite websites directly from your dashboard with our simple embed feature. No need
              to constantly switch back and forth between tabs, just embed your desired website for quick and convenient
              access.
            </p>
            <div className="grid grid-cols-2 gap-6 h-80 sm:h-96 sm:mx-4 p-4 bg-purple-600/10 dark:bg-gray-900/40 rounded-3xl">
              <div className="col-span-2 rounded-lg dark:shadow-[-10px_-10px_80px_-15px_rgba(217,70,239,.5)] bg-purple-600 dark:border border-white/30 flex justify-center items-center">
                <Link
                  target="_blank"
                  to="https://heroicons.com/"
                  rel="noreferrer noopener"
                  variant="naked"
                  className="!text-white/90 hover:!text-white"
                  aria-label="Heroicons"
                >
                  <svg viewBox="0 0 880 166" fill="none" aria-hidden="true" className="h-7">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m78.091 0 5.967 5.676c15.038 14.306 35.323 23.067 57.663 23.067.356 0 .711-.002 1.065-.006l6.363-.08 1.988 6.072a102.026 102.026 0 0 1 5.045 31.782c0 47.391-32.269 87.19-75.928 98.477l-2.163.559-2.163-.559C32.27 153.701 0 113.902 0 66.511c0-11.085 1.769-21.772 5.045-31.782l1.988-6.072 6.363.08c.354.004.71.006 1.065.006 22.34 0 42.625-8.761 57.664-23.067L78.09 0ZM19.846 46.033a84.814 84.814 0 0 0-2.492 20.478c0 38.459 25.662 70.919 60.737 81.006 35.075-10.087 60.738-42.547 60.738-81.006 0-7.071-.866-13.93-2.493-20.478-22.009-1.16-42.166-9.387-58.245-22.453-16.079 13.066-36.235 21.293-58.245 22.453Z"
                      fill="currentColor"
                    />
                    <path
                      d="M275.081 58.045c-10.205 0-18.126 3.828-22.696 10.718V29.564h-19.65v107.184h19.65V95.406c0-13.321 7.159-18.987 16.756-18.987 8.835 0 15.08 5.36 15.08 15.772v44.557h19.65V89.741c0-20.365-12.643-31.696-28.79-31.696Zm59.125 48.539h57.426c.457-2.603.762-5.206.762-8.116 0-22.508-15.994-40.423-38.538-40.423-23.916 0-40.214 17.609-40.214 40.423 0 22.815 16.146 40.424 41.737 40.424 14.623 0 26.048-5.972 33.207-16.384l-15.842-9.187c-3.351 4.441-9.444 7.656-17.06 7.656-10.359 0-18.737-4.287-21.478-14.393Zm-.305-15.312c2.285-9.8 9.444-15.465 19.955-15.465 8.225 0 16.451 4.44 18.888 15.465h-38.843Zm88.817-17.915V60.188h-19.65v76.56h19.65v-36.595c0-16.078 12.948-20.671 23.153-19.446v-22.05c-9.596 0-19.193 4.288-23.153 14.7Zm65.027 65.535c22.392 0 40.366-17.609 40.366-40.424 0-22.814-17.974-40.423-40.366-40.423s-40.214 17.609-40.214 40.423c0 22.815 17.822 40.424 40.214 40.424Zm0-19.293c-11.577 0-20.564-8.728-20.564-21.13 0-12.403 8.987-21.131 20.564-21.131 11.729 0 20.716 8.728 20.716 21.13 0 12.403-8.987 21.131-20.716 21.131Zm60.892-68.598c6.702 0 12.186-5.512 12.186-12.096 0-6.584-5.484-12.25-12.186-12.25-6.55 0-12.034 5.666-12.034 12.25a12.255 12.255 0 0 0 3.561 8.517 12.126 12.126 0 0 0 8.473 3.58Zm-9.749 85.747h19.65v-76.56h-19.65v76.56Zm70.842 2.144c14.928 0 27.876-7.962 34.426-19.905l-17.06-9.8c-3.047 6.278-9.597 10.106-17.518 10.106-11.729 0-20.412-8.728-20.412-20.825 0-12.25 8.683-20.977 20.412-20.977 7.769 0 14.319 3.981 17.365 10.26l16.908-9.954c-6.245-11.79-19.193-19.752-34.121-19.752-23.153 0-40.214 17.609-40.214 40.423 0 22.815 17.061 40.424 40.214 40.424Zm76.833 0c22.391 0 40.366-17.609 40.366-40.424 0-22.814-17.975-40.423-40.366-40.423-22.392 0-40.214 17.609-40.214 40.423 0 22.815 17.822 40.424 40.214 40.424Zm0-19.293c-11.577 0-20.564-8.728-20.564-21.13 0-12.403 8.987-21.131 20.564-21.131 11.729 0 20.716 8.728 20.716 21.13 0 12.403-8.987 21.131-20.716 21.131Zm93.49-61.554c-10.206 0-18.127 3.828-22.697 10.718v-8.575h-19.65v76.56h19.65V95.406c0-13.321 7.159-18.987 16.756-18.987 8.835 0 15.08 5.36 15.08 15.772v44.557h19.65V89.741c0-20.365-12.643-31.696-28.789-31.696ZM840.7 81.93c0-4.134 3.96-6.277 8.835-6.277 5.636 0 9.901 2.909 12.186 7.809l16.756-9.188c-5.941-10.565-16.604-16.23-28.942-16.23-15.69 0-28.942 8.728-28.942 24.346 0 26.949 39.3 20.824 39.3 32.002 0 4.44-4.265 6.584-10.51 6.584-7.617 0-12.796-3.675-14.928-9.953l-17.061 9.647c5.484 11.79 16.756 18.221 31.989 18.221 16.298 0 30.617-7.962 30.617-24.499 0-28.174-39.3-21.13-39.3-32.462Z"
                      fill="currentColor"
                    />
                  </svg>
                </Link>
              </div>
              <div className="rounded-lg dark:shadow-[-10px_-10px_80px_-15px_rgba(217,70,239,.5)] bg-pink-600 dark:border border-white/30 flex justify-center items-center">
                <Link
                  target="_blank"
                  to="https://haikei.app/"
                  rel="noreferrer noopener"
                  variant="naked"
                  className="!text-white/90 hover:!text-white"
                  aria-label="Haikei"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" className="h-9" viewBox="0 0 96 40">
                    <path
                      fill="#fff"
                      d="M87.465 7.456c0-1.538 1.193-2.856 2.888-2.856 1.695 0 2.919 1.318 2.919 2.856 0 1.6-1.224 2.793-2.92 2.793-1.694 0-2.887-1.193-2.887-2.793zM7.623 13.607c1.224-1.255 2.856-1.977 4.865-1.977 3.64 0 6.088 2.605 6.088 6.56v9.665h-4.99v-8.599c0-1.852-1.098-3.107-2.73-3.107-1.946 0-3.233 1.35-3.233 4.394v7.313H2.602V5.258h5.021v8.348z"
                    />
                    <path
                      fill="#fff"
                      fillRule="evenodd"
                      d="M33.552 13.356V12.1h5.021v15.754h-5.021V26.6c-1.224 1.099-2.856 1.726-4.896 1.726-4.174 0-7.69-3.358-7.69-8.348 0-4.959 3.516-8.348 7.69-8.348 2.04 0 3.672.627 4.896 1.726zm-7.658 6.622c0 2.51 1.6 4.08 3.798 4.08 2.008 0 3.86-1.57 3.86-4.08 0-2.48-1.852-4.08-3.86-4.08-2.197 0-3.798 1.6-3.798 4.08z"
                      clipRule="evenodd"
                    />
                    <path
                      fill="#fff"
                      d="M47.472 12.1h-5.021v15.755h5.021V12.1zm20.651 0-6.904 7.501 7.626 8.255h-6.434l-4.833-5.681h-1.193v5.68h-5.021V5.26h5.022v12.208h1.004l4.676-5.366h6.057z"
                    />
                    <path
                      fill="#fff"
                      fillRule="evenodd"
                      d="M84.98 19.978c-.094-5.147-3.672-8.348-8.129-8.348-4.582 0-8.348 3.39-8.348 8.348 0 4.99 3.766 8.348 8.317 8.348 3.61 0 6.465-1.6 7.815-4.927l-4.457-.91c-.847 1.538-2.197 1.82-3.358 1.82-1.695 0-2.982-1.161-3.39-3.044h11.55v-1.287zm-8.13-4.268c1.444 0 2.7.784 3.17 2.48h-6.496c.47-1.602 1.883-2.48 3.326-2.48z"
                      clipRule="evenodd"
                    />
                    <path
                      fill="#fff"
                      d="M87.842 12.1h5.022v15.755h-5.022V12.1zm-42.88 17.652c-1.695 0-2.887 1.318-2.887 2.856 0 1.6 1.192 2.793 2.887 2.793s2.919-1.192 2.919-2.793c0-1.538-1.224-2.856-2.919-2.856z"
                    />
                  </svg>
                </Link>
              </div>

              <div className="rounded-lg dark:shadow-[-10px_-10px_80px_-15px_rgba(217,70,239,.5)] bg-violet-600 dark:border border-white/20 flex justify-center items-center">
                <Link
                  target="_blank"
                  to="https://lucide.dev/"
                  rel="noreferrer noopener"
                  variant="naked"
                  className="!text-white/90 hover:!text-white text-base"
                  aria-label="Lucide"
                >
                  <svg viewBox="0 0 24 24" className="h-12">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14 12a4 4 0 0 0-8 0 8 8 0 1 0 16 0 11.97 11.97 0 0 0-4-8.944" />
                      <path d="M10 12a4 4 0 0 0 8 0 8 8 0 1 0-16 0 11.97 11.97 0 0 0 4.063 9" stroke="#F56565" />
                    </svg>
                  </svg>
                </Link>
              </div>
            </div>
          </Card>
          <Card className="p-6 sm:px-12 sm:py-10 h-full rounded-2xl flex flex-col justify-between group">
            <div>
              <div className="hidden dark:block absolute w-full h-1/2 -top-16 -left-32 bg-gradient-radial from-fuchsia-600/5 via-transparent to-transparent" />

              <h3 className="relative text-2xl font-medium mb-4 tracking-tight">Add markdown</h3>
              <p className="relative text-gray-500 dark:text-gray-400 mb-12">
                Keep track of your todos, compile a list of essential tools for your next project, or{" "}
                <strong className="dark:text-gray-300">store your frequently used code snippets</strong> in a
                user-friendly and easily readable format. Upgrade your productivity and streamline your workflow with
                markdown.
              </p>
            </div>
            <div className="relative -mx-3 sm:mx-0 sm:px-4">
              <div className="hidden dark:block pointer-events-none transition-opacity duration-700 opacity-0 group-hover:opacity-100 absolute w-full h-full scale-[1.8]  bg-gradient-radial from-violet-600/30 via-transparent to-transparent" />

              <div className="shadow-[0px_15px_30px_5px_rgba(0,0,0,.05)] dark:shadow-2xl rounded-lg -m-px overflow-hidden transform-gpu">
                <MarkdownWidget widget={markdownWidgetPreview} />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

const markdownWidgetPreview = {
  information: {
    name: "Docker Cheatsheet",
    markdown:
      "```\n# Build image\ndocker build -t <imageName> .\n\n# Start new container with image\ndocker run -dp 3000:3000 <imageName>\n\n# Get container ids\ndocker ps\n\n# Get logs from container\ndocker logs -f <container>\n\n# SH into running container:\ndocker exec -it <container> bash\n```",
  },
} as Widget
